import { Item, ItemElement, ItemPosition } from '../types';

/**
 * Gets a unique key for an item, preferring value over key
 */
export function getItemKey(item: Item): string {
  return item.value || item.key || '';
}

/**
 * Checks if any items overflow the available width (without offset)
 */
export function hasOverflow(
  positions: Map<string, ItemPosition>,
  constrainedWidth: number,
): boolean {
  let overflow = false;
  positions.forEach((pos) => {
    if (pos.right > constrainedWidth) {
      overflow = true;
    }
  });
  return overflow;
}

/**
 * Calculates which items should be visible based on available width.
 * Uses a two-pass approach:
 * 1. First check if ALL items fit without offset
 * 2. If overflow, reserve space for "More" button and recalculate
 */
export function calculateVisibleItems(
  allItemKeys: string[],
  positions: Map<string, ItemPosition>,
  constrainedWidth: number,
  offsetWidth: number,
): string[] {
  const visibleSet = new Set(allItemKeys);

  // First pass: check if any items overflow WITHOUT reserving space for "More" button
  const hasOverflowDetected = hasOverflow(positions, constrainedWidth);

  // If overflow detected, reserve space for "More" button and recalculate
  if (hasOverflowDetected) {
    const availableWidth = constrainedWidth - offsetWidth;
    positions.forEach((pos, id) => {
      if (pos.right > availableWidth) {
        visibleSet.delete(id);
      }
    });
  }

  return [...visibleSet];
}

/**
 * Caches the positions of all visible items from their DOM elements.
 * Items with 0 width (e.g., display:none) are skipped.
 */
export function cacheItemPositions(
  itemRefs: Map<string, ItemElement>,
  positionsCache: Map<string, ItemPosition>,
): void {
  [...itemRefs.entries()].forEach(([id, el]) => {
    const rect = el.getBoundingClientRect();
    if (rect.width > 0) {
      const pos = {
        left: el.offsetLeft,
        right: el.offsetLeft + rect.width,
      };
      positionsCache.set(id, pos);
    }
  });
}

/**
 * Compares two visibility states to determine if they've changed.
 * Checks both length and content.
 */
export function hasVisibleItemsChanged(
  newVisibleIds: string[],
  currentVisibleIds: string[],
): boolean {
  if (newVisibleIds.length !== currentVisibleIds.length) {
    return true;
  }
  return newVisibleIds.some((id) => !currentVisibleIds.includes(id));
}
